import React, { useEffect, useState } from "react";
import { Modal, ModalBody, ModalContent, ModalHeader } from "components/modal";
import ButtonNew from "components/buttonNew/buttonNew";
import { Checkmark, Close } from "assets/icons";
import { toast } from "react-toastify";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { Input, Select, SelectItem, Card, CardBody, Divider } from "@heroui/react";
import { API_URL } from "constants/index";
import { AddressPicker } from "components/index";
import { LoadingOverlay } from "components/LoadingSpinner";
import { Briefcase, CalendarBlank, IdentificationCard, MapPin } from "@phosphor-icons/react";
import { DatePickerInput } from "shared/components/DatePickerInput/DatePickerInput";

interface AddCompanyModalProps {
  onClose?: () => void;
  isOpen: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  refreshParentCompanies: () => void;
}

const AddCompanyModal: React.FC<AddCompanyModalProps> = ({
  onClose,
  isOpen,
  onOpenChange,
  refreshParentCompanies
}) => {
  const { session } = useSession();
  const [newCompanyName, setNewCompanyName] = useState("");
  const [legalForm, setLegalForm] = useState("");
  const [kbo, setKbo] = useState("");
  const [numberProfessionalSeller, setNumberProfessionalSeller] = useState("");
  const [vat, setVat] = useState("");
  const [address, setAddress] = useState({ description: "", placeId: "" });
  const [foundingDate, setFoundingDate] = useState("");
  const [totalShares, setTotalShares] = useState("");
  const [leiCode, setLeiCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Bedrijf wordt aangemaakt...");

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const handleClose = () => {
    if (isLoading) return;
    onClose?.();
    onOpenChange?.(false);
  };

  const handleCreateCompany = async () => {
    if (!newCompanyName) {
      return toast.error("Gelieve een bedrijfsnaam op te geven");
    }

    if (isLoading) return;
  
    const companyData = {
      name: newCompanyName,
      legalForm,
      kbo,
      numberProfessionalSeller: numberProfessionalSeller,
      vat,
      googlePlaceLongAddress: address.description,
      googlePlaceId: address.placeId,
      foundingDate: foundingDate || undefined,
      totalShares: totalShares ? parseInt(totalShares, 10) : undefined,
      leiCode: leiCode || undefined,
    };
  
    setIsLoading(true);
    
    try {
      setLoadingMessage("Bedrijf wordt aangemaakt...");
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setLoadingMessage("SharePoint map wordt aangemaakt...");
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setLoadingMessage("Registratie in Odoo...\n Dit kan echt enkele minuten duren... Ga intussen even koffie nemen...");
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const response = await fetchWithAuth(
        `${API_URL}/companies/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(companyData),
        },
        session
      );
  
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      
      setLoadingMessage("Aanmaken grootboekrekeningen voor nieuw bedrijf...");
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setLoadingMessage("Aanmaken productsjablonen met multi-company ondersteuning... enkele minuten geduld...");
      await new Promise(resolve => setTimeout(resolve, 2000));
  
      toast.success("Bedrijf succesvol aangemaakt!");
      refreshParentCompanies();
      
      // Reset form
      setNewCompanyName("");
      setLegalForm("");
      setKbo("");
      setNumberProfessionalSeller("");
      setVat("");
      setAddress({ description: "", placeId: "" });
      setFoundingDate("");
      setTotalShares("");
      setLeiCode("");
      
      handleClose();
    } catch (error) {
      console.error("Error creating company:", error);
      toast.error("Fout bij het aanmaken van het bedrijf");
    } finally {
      setIsLoading(false);
      setLoadingMessage("Bedrijf wordt aangemaakt...");
    }
  };

  return (
    <>
      <Modal size="3xl" hideCloseButton className="z-100" isOpen={isOpen}>
        <ModalContent className="max-h-[90vh] overflow-hidden">
          <ModalHeader className="flex flex-col gap-1 border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-moss-primary/10 p-2">
                <Briefcase className="h-5 w-5 text-moss-primary" weight="duotone" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-neutral-primary">Nieuwe vennootschap</h2>
                <p className="text-sm text-gray-500">Vul de gegevens in om een nieuwe vennootschap aan te maken</p>
              </div>
            </div>
          </ModalHeader>
          
          <ModalBody className="flex flex-col gap-6 overflow-y-auto px-6 py-6">
            {/* Basic Information Card */}
            <Card shadow="none" className="border border-gray-200">
              <CardBody className="gap-4 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Briefcase className="h-4 w-4 text-moss-primary" weight="duotone" />
                  <h3 className="text-sm font-semibold text-neutral-primary">Basisinformatie</h3>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Bedrijfsnaam"
                    placeholder="Bijv. Yally NV"
                    value={newCompanyName}
                    onChange={(e) => setNewCompanyName(e.target.value)}
                    disabled={isLoading}
                    isRequired
                    variant="bordered"
                    classNames={{
                      label: "text-sm font-medium",
                      input: "text-sm",
                    }}
                    className="col-span-2"
                  />
                  
                  <Select
                    label="Rechtsvorm"
                    placeholder="Selecteer rechtsvorm"
                    value={legalForm}
                    onChange={(e) => setLegalForm(e.target.value)}
                    isDisabled={isLoading}
                    variant="bordered"
                    classNames={{
                      label: "text-sm font-medium",
                      value: "text-sm",
                    }}
                  >
                    {["NV", "BVBA", "BV"].map((form) => (
                      <SelectItem key={form}>
                        {form}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </CardBody>
            </Card>

            {/* Registration Numbers Card */}
            <Card shadow="none" className="border border-gray-200">
              <CardBody className="gap-4 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <IdentificationCard className="h-4 w-4 text-moss-primary" weight="duotone" />
                  <h3 className="text-sm font-semibold text-neutral-primary">Registratienummers</h3>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="KBO-nummer"
                    placeholder="0123.456.789"
                    value={kbo}
                    onChange={(e) => setKbo(e.target.value)}
                    disabled={isLoading}
                    variant="bordered"
                    classNames={{
                      label: "text-sm font-medium",
                      input: "text-sm",
                    }}
                  />
                  
                  <Input
                    label="BTW-nummer"
                    placeholder="BE0123456789"
                    value={vat}
                    onChange={(e) => setVat(e.target.value)}
                    disabled={isLoading}
                    variant="bordered"
                    classNames={{
                      label: "text-sm font-medium",
                      input: "text-sm",
                    }}
                  />
                  
                  <Input
                    label="Nummer professionele verkoper"
                    placeholder="123456"
                    value={numberProfessionalSeller}
                    onChange={(e) => setNumberProfessionalSeller(e.target.value)}
                    disabled={isLoading}
                    variant="bordered"
                    classNames={{
                      label: "text-sm font-medium",
                      input: "text-sm",
                    }}
                    className="col-span-2"
                  />
                </div>
              </CardBody>
            </Card>

            {/* Location Card */}
            <Card shadow="none" className="border border-gray-200">
              <CardBody className="gap-4 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="h-4 w-4 text-moss-primary" weight="duotone" />
                  <h3 className="text-sm font-semibold text-neutral-primary">Locatie</h3>
                </div>
                
                <div className={isLoading ? "opacity-50 pointer-events-none" : ""}>
                  <AddressPicker
                    editMode={true}
                    defaultAddress={address.description}
                    getAddress={(selectedPlace) => setAddress(selectedPlace)}
                  />
                </div>
              </CardBody>
            </Card>

            {/* Extra Company Info Card */}
            <Card shadow="none" className="border border-gray-200">
              <CardBody className="gap-4 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarBlank className="h-4 w-4 text-moss-primary" weight="duotone" />
                  <h3 className="text-sm font-semibold text-neutral-primary">Extra gegevens</h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium text-foreground">Oprichtingsdatum</span>
                    <DatePickerInput
                      value={foundingDate}
                      onChange={(value) => setFoundingDate(value)}
                      disabled={isLoading}
                      placeholder="Oprichtingsdatum"
                    />
                  </div>

                  <Input
                    label="Totaal aantal aandelen"
                    placeholder="1000"
                    type="number"
                    value={totalShares}
                    onChange={(e) => setTotalShares(e.target.value)}
                    disabled={isLoading}
                    variant="bordered"
                    classNames={{
                      label: "text-sm font-medium",
                      input: "text-sm",
                    }}
                  />

                  <Input
                    label="LEI-code"
                    type="text"
                    placeholder="LEI-code"
                    value={leiCode}
                    onChange={(e) => setLeiCode(e.target.value)}
                    disabled={isLoading}
                    variant="bordered"
                    classNames={{
                      label: "text-sm font-medium",
                      input: "text-sm",
                    }}
                  />
                </div>
              </CardBody>
            </Card>
          </ModalBody>

          <Divider />
          
          <div className="flex items-center justify-end gap-2 px-6 py-4 bg-gray-50">
            <ButtonNew
              variant="secondaryNeutralMid"
              onClick={handleClose}
              icon={<Close />}
              label="Annuleren"
              isDisabled={isLoading}
            />
            <ButtonNew
              variant="primaryMoss"
              onClick={handleCreateCompany}
              icon={<Checkmark />}
              label="Vennootschap aanmaken"
              isDisabled={isLoading || !newCompanyName}
            />
          </div>
        </ModalContent>
      </Modal>
      
      {isLoading && (
        <LoadingOverlay 
          message={loadingMessage} 
          size="lg" 
        />
      )}
    </>
  );
};

export default AddCompanyModal;

